import { View, Text, StyleSheet, ScrollView, Pressable, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { ArrowLeft } from 'lucide-react-native';
import { theme } from '@/constants/theme';
import { AccessibilityButton } from '@/components/AccessibilityButton';

export default function AccessibilityStatementScreen() {
  const router = useRouter();

  return (
    <View style={styles.container}>
      {/* SEO/GEO FIX: add page-level canonical metadata for legal pages included in sitemap. */}
      {Platform.OS === 'web' && (
        <Head>
          <title>הצהרת נגישות | Kursi</title>
          <meta name="description" content="הצהרת הנגישות של Kursi, כולל התאמות שבוצעו ודרכי יצירת קשר בנושא נגישות." />
          <meta name="robots" content="index,follow" />
          <link rel="canonical" href="https://kursiaii.com/legal/accessibility" />
          <meta property="og:title" content="הצהרת נגישות | Kursi" />
          <meta property="og:description" content="הצהרת הנגישות של Kursi, כולל התאמות שבוצעו ודרכי יצירת קשר בנושא נגישות." />
          <meta property="og:url" content="https://kursiaii.com/legal/accessibility" />
        </Head>
      )}

      <View style={styles.header}>
        <Pressable onPress={() => router.back()} style={styles.backButton}>
          <ArrowLeft color={theme.colors.text} size={24} />
        </Pressable>
        <Text style={styles.headerTitle}>הצהרת נגישות</Text>
        <View style={styles.placeholder} />
      </View>

      <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
        <Text style={styles.title}>הצהרת נגישות</Text>

        <View style={styles.section}>
          <Text style={styles.paragraph}>
            אנחנו בקורסי" - פלטפורמה שמספקת שירות טכנולוגי לאיתור, סינון, דירוג והתאמה של קורסים והכשרות מקצועיות, באמצעות מערכת מבוססת בינה מלאכותית (להלן: "הפלטפורמה"), מחויבים ופועלים למען קידום הנגישות ולשיפור חוויית הגלישה עבור כלל משתמשי הפלטפורמה, לרבות אנשים עם מוגבלויות. אנו פועלים ליישום התאמות נגישות בהתאם לעקרונות תקנות שוויון זכויות לאנשים עם מוגבלות (התאמות נגישות לשירות), התשע"ג-2013 (סימן ג' – נגישות שירותי אינטרנט), ככל הניתן ובהתאם ליכולות הפלטפורמה, וזאת מתוך אמונה חזקה שלכל אדם יש את הזכות לחיות בכבוד, בשוויון, בנוחות ובעצמאות.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.paragraph}>
            השקענו משאבים כדי להבטיח שהפלטפורמה תהיה קלה יותר לשימוש ונגישה יותר לאנשים עם מוגבלויות, בין היתר: תכני הפלטפורמה נכתבו בשפה ברורה ונעשה שימוש בפונטים קריאים; התמצאות בפלטפורמה היא פשוטה ונוחה וכוללת תפריטים זמינים וברורים; הקישורים בפלטפורמה ברורים ומסבירים להיכן מועברים לאחר לחיצה עליהם; הפלטפורמה גם מותאמת לסביבת עבודה ברזולוציות שונות.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.paragraph}>
            כמו כן, ביצענו התאמות נגישות בפלטפורמה בהתאם לדרישות הדין הישראלי, לרבות תקנות שוויון זכויות לאנשים עם מוגבלות (התאמות נגישות לשירות), תשע"ג-2013, ובהתאם לעקרונות תקן 5568 – נגישות תכנים באינטרנט (מבוסס WCAG 2.0 רמה AA). להלן פירוט התאמות נגישות שבוצעו בפלטפורמה:
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.listItem}>ניווט מלא באמצעות מקלדת – כל רכיבי האתר ניתנים לתפעול באמצעות מקשי המקלדת בלבד, ללא צורך בעכבר, לרבות תפריטים, טפסים וקישורים.</Text>
          <Text style={styles.listItem}>כותרות ברורות ומובנות (H1, H2, H3) – מבנה הכותרות באתר מסודר באופן היררכי המאפשר ניווט יעיל באמצעות טכנולוגיות מסייעות.</Text>
          <Text style={styles.listItem}>ניגודיות צבעים מספקת – הטקסטים, הכפתורים והאלמנטים הגרפיים עומדים בדרישות הניגודיות המומלצות, כדי להבטיח קריאות מיטבית.</Text>
          <Text style={styles.listItem}>אפשרויות התאמת גודל גופן – ניתן להגדיל את הטקסט עד 200% באמצעות הגדרות הדפדפן, מבלי לפגוע בתצוגה או בתפקוד האתר.</Text>
          <Text style={styles.listItem}>טקסט חלופי (Alt Text) לתמונות – לכל תמונה באתר מוצמד תיאור טקסטואלי חלופי המאפשר לקוראי מסך להבין את תוכנה.</Text>
          <Text style={styles.listItem}>מבנה ותוכן קוהרנטי וקל להבנה – התוכן באתר מאורגן באופן ברור, עקבי ומובנה, המקל על הבנה וניווט עבור משתמשים עם מוגבלויות קוגניטיביות.</Text>
          <Text style={styles.listItem}>סמלים ברורים ופשוטים – נעשה שימוש באייקונים ברורים, אחידים ופשוטים להבנה, המאפשרים זיהוי מהיר של פעולות ותפריטים.</Text>
          <Text style={styles.listItem}>ניווט עקבי – מבנה האתר נשמר עקבי בין הדפים, כך שמשתמשים יכולים לאתר מידע ולבצע פעולות בקלות.</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.paragraph}>
            אף שאנו שואפים לעמוד בכל דרישות תקן WCAG 2.0 רמה AA, בהתאם לתקן הישראלי 5568, יש לציין כי ייתכן כי חלקים מסוימים בפלטפורמה עדיין דורשים התאמות נוספות ו/או עשויים לדרוש שיפורים. אנו פועלים באופן מתמשך לאיתור ולתיקון ליקויי נגישות, ומתחייבים לבצע התאמות סבירות ככל הנדרש.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.paragraph}>
            אנו מקדמים בברכה פניות, הערות והצעות לשיפור נגישות הפלטפורמה. אם נתקלתם בקושי כלשהו או ברצונכם לדווח על בעיית נגישות, ניתן לפנות אלינו בכתובת: kursi.online.il@gmail.com.
          </Text>
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>
            ברצוננו להבהיר כי תכנים המופיעים בפלטפורמה ומקורם בצד שלישי (כגון תוספים, מערכות חיצוניות או קישורים לאתרים אחרים) עשויים שלא לעמוד באופן מלא בדרישות התקן, ואינם נמצאים בשליטתנו.
          </Text>
        </View>
      </ScrollView>

      <AccessibilityButton />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.md,
    paddingTop: Platform.OS === 'ios' ? 50 : theme.spacing.lg,
    backgroundColor: theme.colors.backgroundLight,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  backButton: {
    padding: theme.spacing.xs,
  },
  headerTitle: {
    fontSize: 18,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
  },
  placeholder: {
    width: 40,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: theme.spacing.lg,
    paddingBottom: theme.spacing.xl * 2,
  },
  title: {
    fontSize: 28,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
    textAlign: 'center',
    marginBottom: theme.spacing.xl,
  },
  section: {
    marginBottom: theme.spacing.lg,
  },
  paragraph: {
    fontSize: 16,
    fontFamily: theme.fonts.regular,
    color: theme.colors.text,
    lineHeight: 24,
    textAlign: 'right',
    marginBottom: theme.spacing.md,
  },
  listItem: {
    fontSize: 16,
    fontFamily: theme.fonts.regular,
    color: theme.colors.text,
    lineHeight: 24,
    textAlign: 'right',
    marginBottom: theme.spacing.sm,
    paddingRight: theme.spacing.sm,
  },
  footer: {
    backgroundColor: theme.colors.secondary,
    padding: theme.spacing.lg,
    borderRadius: theme.borderRadius.md,
    marginTop: theme.spacing.xl,
  },
  footerText: {
    fontSize: 14,
    fontFamily: theme.fonts.medium,
    color: theme.colors.textLight,
    textAlign: 'center',
    lineHeight: 20,
  },
});